// Four independent facts about a tour, for the browser. Mirrors functions/lib/tourState.js;
// keep the two in step.
//
//   phase       where the operation is       tourPhases.js, moved by the rules and by hand
//   visibility  who can see the page         public | hidden | restricted
//   status      whether they can book        open | full | closed
//   type        what kind of departure       public | private | partner
//   cancelledAt the departure was called off a timestamp, or absent
//
// Before 2026-09-18 one `status` field carried visibility, sales and lifecycle at once
// (draft, open, full, private, in_progress, completing, complete, archived, cancelled).
// `tourState()` reads either shape so nothing breaks on a document the migration has not
// reached; `proposeTourState()` is the migration, run from Technical → Configuration →
// Migration. [Aaron, 2026-09-18]

import { phaseOf, PHASES } from './tourPhases'

export const VISIBILITIES = Object.freeze(['public', 'hidden', 'restricted'])
export const VISIBILITY_OPTIONS = Object.freeze([
  { value: 'public', text: 'Public', description: 'Listed on the website; anyone can see it and book it.' },
  { value: 'hidden', text: 'Hidden', description: 'Not listed, but anyone with the link can see it and book it. Private groups.' },
  { value: 'restricted', text: 'Restricted', description: 'Only signed-in staff can see it. New tours start here.' },
])

export const STATUSES = Object.freeze(['open', 'full', 'closed'])
export const STATUS_OPTIONS = Object.freeze([
  { value: 'open', text: 'Open', description: 'Spaces available; bookings are taken.' },
  { value: 'full', text: 'Full', description: 'No spaces, but an arrangement may still be possible. Shown as full; no self-serve booking.' },
  { value: 'closed', text: 'Closed', description: 'No new bookings at all.' },
])

export const TYPES = Object.freeze(['public', 'private', 'partner'])
export const TYPE_OPTIONS = Object.freeze([
  { value: 'public', text: 'Public', description: 'A scheduled departure anyone may join.' },
  { value: 'private', text: 'Private', description: 'Run for one group.' },
  { value: 'partner', text: 'Partner', description: 'Sold through a partner.' },
])

/** Phases in which a booking may still be taken; from Operating on, nothing new. */
export const BOOKABLE_PHASES = Object.freeze(['setup', 'booking', 'planning', 'pre_tour'])

const str = (v) => (typeof v === 'string' ? v.trim().toLowerCase() : '')

/** What the old single `status` meant, as the three new facts. */
export const LEGACY_STATUS = Object.freeze({
  draft: { visibility: 'restricted', status: 'open' },
  open: { visibility: 'public', status: 'open' },
  full: { visibility: 'public', status: 'full' },
  private: { visibility: 'hidden', status: 'open' },
  in_progress: { visibility: 'public', status: 'closed' },
  completing: { visibility: 'public', status: 'closed' },
  complete: { visibility: 'public', status: 'closed' },
  completed: { visibility: 'public', status: 'closed' },
  archived: { visibility: 'public', status: 'closed' },
  cancelled: { visibility: 'restricted', status: 'closed', cancelled: true },
  canceled: { visibility: 'restricted', status: 'closed', cancelled: true },
})

/** True once the document carries the new fields. */
export function isMigrated(tour) {
  return VISIBILITIES.includes(str(tour && tour.visibility)) && STATUSES.includes(str(tour && tour.status))
}

/** The template's `type` ('open' | 'private') as a tour type. */
export function typeFromTemplate(template) {
  const t = str(template && template.type)
  if (t === 'private' || (template && template.private === true)) return 'private'
  return 'public'
}

/**
 * The tour's state, whichever shape the document has.
 * @returns {{ visibility: string, status: string, type: string, cancelled: boolean, legacy: boolean }}
 */
export function tourState(tour) {
  const t = tour || {}
  const type = TYPES.includes(str(t.type)) ? str(t.type) : (t.private === true ? 'private' : 'public')
  if (isMigrated(t)) {
    return { visibility: str(t.visibility), status: str(t.status), type, cancelled: Boolean(t.cancelledAt), legacy: false }
  }
  const old = LEGACY_STATUS[str(t.status)] || { visibility: 'restricted', status: 'open' }
  const visibility = t.private === true && old.visibility === 'public' ? 'hidden' : old.visibility
  return { visibility, status: old.status, type, cancelled: Boolean(t.cancelledAt) || old.cancelled === true, legacy: true }
}

/** Listed on the public website: public, not cancelled, not archived. */
export function isListedPublicly(tour) {
  const s = tourState(tour)
  return s.visibility === 'public' && !s.cancelled && phaseOf(tour) !== 'archived'
}

/**
 * May this viewer see the tour's page? `employee` is a signed-in staff member; `viaLink`
 * means the page was reached by its own address rather than a listing.
 */
export function canViewTour(tour, { employee = false, viaLink = true } = {}) {
  const s = tourState(tour)
  if (employee) return true
  if (s.visibility === 'public') return true
  if (s.visibility === 'hidden') return viaLink
  return false
}

/**
 * May a booking be taken now? One rule for the website, the booking flow, the catalog
 * and the cache: not cancelled, status open, and a phase before Operating.
 * @returns {{ ok: boolean, reason: string }}
 */
export function isTourBookable(tour, { employee = false } = {}) {
  if (!tour) return { ok: false, reason: 'Tour not found' }
  const s = tourState(tour)
  const phase = phaseOf(tour)
  if (s.cancelled) return { ok: false, reason: 'This tour has been cancelled' }
  if (phase === 'archived') return { ok: false, reason: 'This tour is archived' }
  if (!canViewTour(tour, { employee })) return { ok: false, reason: 'This tour is not available' }
  if (s.status === 'full') return { ok: false, reason: 'This tour is full' }
  if (s.status !== 'open') return { ok: false, reason: 'This tour is not open for booking' }
  if (!BOOKABLE_PHASES.includes(phase)) return { ok: false, reason: 'This tour has already started' }
  return { ok: true, reason: '' }
}

/**
 * The website's one-word label for a departure. Cancelled and closed read CLOSED; a tour
 * past its running reads COMPLETE with its year once that year has gone.
 */
export function publicLabel(tour, { remaining = null } = {}) {
  const s = tourState(tour)
  const phase = phaseOf(tour)
  if (s.cancelled) return 'CLOSED'
  if (phase === 'archived') return 'ARCHIVED'
  if (phase === 'complete' || phase === 'wrap_up') {
    const start = tour?.schedule?.primary?.start || tour?.schedule?.start
    const year = start ? new Date(start).getFullYear() : NaN
    return Number.isFinite(year) && year < new Date().getFullYear() ? `COMPLETE - ${year}` : 'COMPLETE'
  }
  if (phase === 'operating') return 'IN PROGRESS'
  if (s.status === 'full') return 'FULL'
  if (s.status === 'closed') return 'CLOSED'
  if (s.visibility === 'hidden') return 'PRIVATE TOUR'
  const left = remaining === null ? Number(tour?.availability?.remaining || 0) : Number(remaining)
  if (left === 0) return 'LIMITED AVAILABILITY'
  if (left <= 6) return 'OPEN'
  if (left <= 11) return 'ALMOST FULL'
  return 'OPEN'
}

/**
 * The migration for one document: the new fields the old `status` implies, or null when
 * the document already carries them. `template` supplies the type.
 */
export function proposeTourState(tour, template = null) {
  if (!tour || isMigrated(tour)) return null
  const s = tourState(tour)
  const type = TYPES.includes(str(tour.type)) ? str(tour.type) : typeFromTemplate(template || tour.template)
  const out = { visibility: s.visibility, status: s.status, type, legacyStatus: str(tour.status) || null }
  if (s.cancelled && !tour.cancelledAt) out.cancelledAt = 'now'
  return out
}

export { PHASES }
